import { Suspense } from "react"
import { DateTime } from "luxon"
import { Grid } from "@mui/material"
import { enqueueSnackbar } from "notistack"
import { makeStyles } from "tss-react/mui"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { CreateUpdateDayMenuForm } from "./CreateUpdateDayMenuForm"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    MenuPlaningQuery,
    createDayMenu,
    fetchMenuDetails,
    updateDayMenu,
    fetchMerchantConfig,
    saveMerchantConfig,
} from "../../menuPlanningApi"
import { MenuDetails, MerchantConfig, SignageType } from "../../menuPlanningTypes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { toDinero, toNumberLossy } from "../../../../../currency/currencyLib"
import { colorOptions } from "../helpers"

type DayMenuDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    menuId: null | number
    dateInfo: DateTime
}>

const useStyles = makeStyles()((theme) => ({
    container: {
        paddingBottom: theme.spacing(6),
    },
}))

const selectMerchantMenuPlanningSignageType = (response: MerchantConfig) => {
    const signageConfig = response?.config?.merchantSettings?.find(
        (setting: any) => setting.name === "MENU_PLANNING_SIGNAGE_TYPES",
    )
    return signageConfig?.value?.split(",") as SignageType[]
}

export function DayMenuDialog({ open, onClose, menuId, dateInfo }: DayMenuDialogProps) {
    const { classes } = useStyles()

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            maxWidthProp="md"
            dialogTitle={
                menuId ? (
                    <LocalizedStrict id="merchant-menu-planning-dialog-edit-title">
                        <>Edit menu</>
                    </LocalizedStrict>
                ) : (
                    <LocalizedStrict id="merchant-menu-planning-dialog-create-title">
                        <>Create menu</>
                    </LocalizedStrict>
                )
            }
        >
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                direction="column"
                wrap="nowrap"
                className={classes.container}
            >
                <QueryErrorBoundary>
                    <Suspense fallback={<LoadingSpinner />}>
                        {menuId ? (
                            <DayMenuEdit onClose={onClose} menuId={menuId} />
                        ) : (
                            <DayMenuCreate onClose={onClose} dateInfo={dateInfo} />
                        )}
                    </Suspense>
                </QueryErrorBoundary>
            </Grid>
        </StandardDialog>
    )
}

// CREATE MENU
type DayMenuCreateProps = Readonly<{
    onClose: () => void
    dateInfo: DateTime
}>

const DayMenuCreate = ({ onClose, dateInfo }: DayMenuCreateProps) => {
    const { merchantId } = useMerchant()

    const { mutateAsync: callCreateDayMenu } = useApiMutation(createDayMenu, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MenuPlaningQuery.LIST] })
            if ("serviceResponse" in data && data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse.displayMessage,
                })
            }
            onClose()
        },
    })

    const merchantConfig = useApiQuery(fetchMerchantConfig, {
        queryName: MenuPlaningQuery.MERCHANT_CONFIG,
        dependencies: {
            merchantId,
        },
        select: selectMerchantMenuPlanningSignageType,
    })

    const { mutate } = useApiMutation(saveMerchantConfig, {})

    return (
        <CreateUpdateDayMenuForm
            dateInfo={dateInfo}
            signageTypes={merchantConfig.data}
            onSaveSignageTypes={(selectedSignageTypes: string[]) =>
                mutate({
                    merchantId,
                    property: {
                        name: "MENU_PLANNING_SIGNAGE_TYPES",
                        value: selectedSignageTypes.join(","),
                    },
                })
            }
            onSubmit={(data) => {
                merchantId && callCreateDayMenu({ menu: data, merchantId })
            }}
        />
    )
}

// EDIT MENU
type DayMenuEditProps = Readonly<{
    onClose: () => void
    menuId: number
}>
const selectMenu = (data: MenuDetails) => {
    if (!data) return null
    const { id, merchant, publishDate, recurrence, dishes, color, ...restValues } = data
    return {
        ...restValues,
        dishes: dishes.map((item) => {
            return {
                ...item,
                price: item.price ? toNumberLossy(toDinero(item.price)) : undefined,
                currency: item.price?.currency,
            }
        }),
        color: colorOptions.find((el) => el.code === color) ?? null,
        publishDate: DateTime.fromISO(publishDate),
        ...(recurrence
            ? {
                  recurrence: {
                      ...recurrence,
                      fromDate: recurrence?.fromDate ? DateTime.fromISO(recurrence?.fromDate) : undefined,
                      toDate: recurrence?.toDate ? DateTime.fromISO(recurrence?.toDate) : undefined,
                  },
              }
            : {}),
    }
}
const DayMenuEdit = ({ onClose, menuId }: DayMenuEditProps) => {
    const menuResult = useApiQuery(fetchMenuDetails, {
        queryName: MenuPlaningQuery.DETAILS,
        dependencies: {
            menuId,
        },
        select: selectMenu,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutateAsync: callEditDayMenu } = useApiMutation(updateDayMenu, {
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MenuPlaningQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(MenuPlaningQuery.DETAILS, { menuId: params.menuId }),
            })
            if ("serviceResponse" in data && data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse.displayMessage,
                })
            }
            onClose()
        },
    })

    return (
        <CreateUpdateDayMenuForm
            initialValues={menuResult.data}
            onSubmit={(data) => {
                menuId && callEditDayMenu({ menu: data, menuId })
            }}
        />
    )
}
